import { useState } from 'react';
import { cn } from '@/lib/cn';
import {
  BLOG_PATH,
  SITE_URL,
  formatPostDate,
  postPath,
  readingMinutes,
  type BlogPost,
} from '../lib/blog';
import { QUALIFIER_PATH, SALES_EMAIL } from '../lib/links';
import checkmark from '../assets/v2-checkmark-circle.svg';
import { BlogBlocks } from './BlogBlocks';

/**
 * A single blog post at /blog/<slug>/.
 *
 * The page's <head> (title, description, canonical, Open Graph, JSON-LD) is NOT
 * set here — it is written into the post's own HTML shell at build time by the
 * SEO plugin in vite.landing.config.ts, so crawlers and LinkedIn/X previews see
 * it without running JavaScript.
 *
 * Every CTA leads somewhere measurable: the pilot-partner buttons go to the
 * /qualifier funnel (HubSpot), and the PDF is a plain, shareable static URL.
 */

const primaryBtn =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-6 py-3.5 text-base font-semibold text-white no-underline transition-colors hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700';
const outlineBtn =
  'inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-landing-outline px-6 py-3.5 text-base font-semibold text-landing-outline no-underline transition-colors hover:bg-landing-band focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function DownloadIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="size-5"
    >
      <path d="M10 3v10m0 0-4-4m4 4 4-4M4 15.5V17h12v-1.5" strokeLinecap="round" />
    </svg>
  );
}

function Avatar({ name, size }: { name: string; size: 'sm' | 'lg' }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-landing-demo-band font-bold text-white',
        size === 'sm' ? 'size-11 text-sm' : 'size-16 text-lg',
      )}
    >
      {initials(name)}
    </span>
  );
}

function ShareLinks({ post }: { post: BlogPost }) {
  const [copied, setCopied] = useState(false);
  const url = `${SITE_URL}${postPath(post)}`;
  const enc = encodeURIComponent;

  const links = [
    {
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    },
    { label: 'X', href: `https://x.com/intent/post?url=${enc(url)}&text=${enc(post.title)}` },
    {
      label: 'Email',
      href: `mailto:?subject=${enc(post.title)}&body=${enc(`${post.dek}\n\n${url}`)}`,
    },
  ];

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked (insecure context, permissions) — the
      // address bar still has the URL, so failing quietly is fine.
    }
  }

  const chip =
    'inline-flex items-center rounded-full border border-landing-card-line px-4 py-2 text-sm font-medium text-landing-outline no-underline transition-colors hover:border-landing-outline hover:bg-landing-band';

  return (
    <div className="mt-14 flex flex-wrap items-center gap-3 border-t border-landing-card-line pt-8">
      <span className="mr-1 text-sm font-semibold text-landing-slate">Share this article</span>
      {links.map((l) => (
        <a
          key={l.label}
          href={l.href}
          target={l.label === 'Email' ? undefined : '_blank'}
          rel="noreferrer"
          className={chip}
        >
          {l.label}
        </a>
      ))}
      <button type="button" onClick={copy} className={chip}>
        <span aria-live="polite">{copied ? 'Link copied' : 'Copy link'}</span>
      </button>
    </div>
  );
}

const DEFAULT_CTA = {
  eyebrow: 'Talk to us',
  title: 'See what WisdomX Orbis would find in your estate.',
  body: 'Answer six quick questions and we will recommend the right starting point for your organisation.',
};

export function BlogArticle({ post }: { post: BlogPost }) {
  const cta = post.cta ?? DEFAULT_CTA;
  const minutes = readingMinutes(post);

  return (
    <main className="w-full bg-white">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="w-full bg-gradient-to-b from-landing-peach to-white px-5 pb-10 pt-10 sm:px-6 md:px-8 lg:pb-14 lg:pt-14">
        <div className="mx-auto max-w-[760px]">
          <nav aria-label="Breadcrumb" className="mb-8 text-sm">
            <ol className="m-0 flex list-none flex-wrap items-center gap-2 p-0 text-landing-deep-muted">
              <li>
                <a href="/" className="no-underline hover:text-landing-slate hover:underline">
                  Home
                </a>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <a
                  href={BLOG_PATH}
                  className="no-underline hover:text-landing-slate hover:underline"
                >
                  Blog
                </a>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="font-medium text-landing-slate">
                {post.category}
              </li>
            </ol>
          </nav>

          <p className="mb-4 inline-flex rounded-full bg-landing-icon-tint px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-landing-accent">
            Thought leadership · {post.category}
          </p>

          <h1 className="m-0 text-[clamp(32px,5vw,52px)] font-extrabold leading-[1.1] tracking-[-1px] text-landing-slate">
            {post.title}
          </h1>
          <p className="mb-0 mt-5 text-xl leading-[1.5] text-landing-outline lg:text-[22px]">
            {post.dek}
          </p>

          <div className="mt-8 flex items-center gap-3">
            <Avatar name={post.author.name} size="sm" />
            <div className="text-sm leading-[1.5]">
              <p className="m-0 font-semibold text-landing-slate">{post.author.name}</p>
              <p className="m-0 text-landing-deep-muted">
                <time dateTime={post.publishedAt}>{formatPostDate(post.publishedAt)}</time>
                {' · '}
                {minutes} min read
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            {post.pdf && (
              <a href={post.pdf.href} target="_blank" rel="noreferrer" className={primaryBtn}>
                <DownloadIcon />
                {post.pdf.label}
              </a>
            )}
            <a href={QUALIFIER_PATH} className={outlineBtn}>
              {cta.eyebrow}
            </a>
          </div>
        </div>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div className="px-5 pb-16 sm:px-6 md:px-8 lg:pb-24">
        <article className="mx-auto max-w-[760px]">
          {post.highlights && (
            <aside className="mb-14 rounded-2xl border border-landing-card-line bg-landing-band p-6 lg:p-8">
              <p className="mb-4 mt-0 text-sm font-semibold uppercase tracking-[0.12em] text-landing-slate">
                In this article
              </p>
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {post.highlights.map((h) => (
                  <li key={h} className="flex gap-3 text-base leading-[1.5] text-landing-outline">
                    <img
                      src={checkmark}
                      alt=""
                      width={20}
                      height={20}
                      className="mt-0.5 size-5 shrink-0"
                    />
                    {h}
                  </li>
                ))}
              </ul>
            </aside>
          )}

          <BlogBlocks blocks={post.blocks} />

          <ShareLinks post={post} />

          {/* ── CTA band ───────────────────────────────────────────────── */}
          <section
            aria-labelledby="post-cta-title"
            className="mt-12 rounded-3xl bg-landing-demo-band px-6 py-10 text-white lg:px-12 lg:py-12"
          >
            <p className="mb-3 mt-0 text-sm font-semibold uppercase tracking-[0.12em] text-primary-300">
              {cta.eyebrow}
            </p>
            <h2
              id="post-cta-title"
              className="m-0 text-[26px] font-bold leading-[1.25] tracking-[-0.5px] lg:text-[32px]"
            >
              {cta.title}
            </h2>
            <p className="mb-0 mt-4 text-base leading-[1.7] text-white/80 lg:text-lg">{cta.body}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a href={QUALIFIER_PATH} className={primaryBtn}>
                Get started
              </a>
              <a
                href={`mailto:${SALES_EMAIL}?subject=${encodeURIComponent(`Enquiry: ${post.title}`)}`}
                className="inline-flex items-center justify-center rounded-lg border-[1.5px] border-white/70 px-6 py-3.5 text-base font-semibold text-white no-underline transition-colors hover:bg-white/10"
              >
                Email {SALES_EMAIL}
              </a>
            </div>
          </section>

          {/* ── About the author ───────────────────────────────────────── */}
          <section
            aria-label="About the author"
            className="mt-12 flex flex-col gap-4 rounded-2xl border border-landing-card-line p-6 sm:flex-row lg:p-8"
          >
            <Avatar name={post.author.name} size="lg" />
            <div>
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.12em] text-landing-deep-muted">
                About the author
              </p>
              <p className="mb-0 mt-1 text-lg font-bold text-landing-slate">{post.author.name}</p>
              <p className="m-0 text-sm text-landing-deep-muted">{post.author.role}</p>
              <p className="mb-0 mt-3 text-[15px] leading-[1.7] text-landing-outline">
                {post.author.bio}
              </p>
            </div>
          </section>

          {post.sources && (
            <p className="mb-0 mt-10 text-xs leading-[1.6] text-landing-deep-muted">
              {post.sources}
            </p>
          )}

          <a
            href={BLOG_PATH}
            className="mt-10 inline-flex items-center gap-1.5 text-sm font-semibold text-landing-accent no-underline hover:underline"
          >
            <span aria-hidden="true">←</span> All articles
          </a>
        </article>
      </div>
    </main>
  );
}
